import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, Collapse, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import FileNode from './FileNode'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FileFavoritesModel } from '../../models'
import useResizeObserver from 'use-resize-observer'

interface FileFavoritesViewProps {
  files: FileFavoritesModel[]
}

const useStyles = makeStyles()(theme => ({
  paper: {
    marginBottom: theme.spacing(2),
  },
  paperContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  fileNodeWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3,minmax(251px,1fr))',
    gridAutoRows: 'auto',
    gap: theme.spacing(2),

    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(auto-fill,minmax(251px,1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
}))

const COLLAPSED_SIZE = 150

const FileFavoritesView: React.FC<FileFavoritesViewProps> = ({ files }) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()
  const [showMore, setShowMore] = useState<boolean>(false)
  const [showButton, setShowButton] = useState<boolean>(false)
  const boxRef = useRef<HTMLDivElement>(null)

  const updateButtonState = (): void => {
    if (boxRef.current) {
      const contentHeight = boxRef.current.scrollHeight
      setShowButton(contentHeight > COLLAPSED_SIZE)
    }
  }

  useResizeObserver<HTMLDivElement>({
    ref: boxRef,
    onResize: () => updateButtonState(),
  })

  useEffect(() => {
    updateButtonState()
  }, [files])

  const toggleShowFiles = (): void => {
    setShowMore(!showMore)
  }

  return (
    <Paper
      contentClassName={classes.paperContent}
      className={classes.paper}
      title={
        <Typography sx={{ fontWeight: 'bold' }} variant="body1" component={'span'}>
          {t('documents.favorites.title')}
        </Typography>
      }
    >
      <Collapse collapsedSize={COLLAPSED_SIZE} in={showMore}>
        <Box ref={boxRef} className={cx(classes.fileNodeWrapper)}>
          {files.map(file => {
            return <FileNode key={file.fileInfoId} file={file} />
          })}
        </Box>
      </Collapse>
      {showButton && (
        <Button variant="text" onClick={toggleShowFiles} sx={{ alignSelf: 'flex-end' }}>
          {!showMore ? t('documents.favorites.showMore') : t('documents.favorites.showLess')}
        </Button>
      )}
    </Paper>
  )
}

export default FileFavoritesView
